import { useMutation } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import {
  INCIDENTS_LIST_QUERY_KEY,
  INCIDENTS_TYPES_LIST_QUERY_KEY,
} from '@/modules/common/queries/incidents.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY } from '@/modules/admin-dashboard/queries/tasks.ts';
import {
  CreateIncidentTypeRequest,
  ResolveIncidentResultResultRequest,
  UpdateIncidentResultRequest,
  UpdateIncidentTypeRequest,
} from 'compliance-api';

export const useCreateIncidentTypeMutation = () => {
  return useMutation({
    mutationFn: async (payload: CreateIncidentTypeRequest) => {
      const response = await withErrorHandling(api.v1.createV1IncidentTypesPost(payload), {
        loading: 'Creating event type...',
        success: 'Event type created',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
    },
  });
};

export const useUpdateIncidentTypeMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateIncidentTypeRequest) => {
      const response = await withErrorHandling(api.v1.updateV1IncidentTypesPatch(payload), {
        loading: 'Updating event type...',
        success: 'Event type updated',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteIncidentTypeMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(api.v1.removeV1IncidentTypesIdDelete({ id }), {
        loading: 'Archiving event type...',
        success: 'Event type archived',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
    },
  });
};

export const useRestoreIncidentTypeMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.restoreIncidentTypeV1IncidentTypesRestorePost({ id }),
        {
          loading: 'Restoring event type...',
          success: 'Event type restored',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
    },
  });
};

export const useResolveIncidentMutation = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  return useMutation({
    mutationFn: async (payload: ResolveIncidentResultResultRequest) => {
      const response = await withErrorHandling(
        api.v1.resolveIncidentResultV1IncidentResultsResolvePatch(payload),
        {
          loading: 'Resolving event...',
          success: 'Event resolved',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
      onSuccess && onSuccess();
    },
  });
};

export const useUpdateIncidentMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateIncidentResultRequest) => {
      const response = await withErrorHandling(
        api.v1.updateIncidentResultV1IncidentResultsPatch(payload),
        {
          loading: 'Updating event...',
          success: 'Event updated',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
    },
  });
};

export const useDeleteIncidentMutation = (options?: {
  setErrors?: (errors: FormikErrors<FormikValues>) => void;
}) => {
  return useMutation({
    mutationFn: async (payload: { id: number }) => {
      const response = await withErrorHandling(
        api.v1.deleteIncidentResultV1IncidentResultsIdDelete({ id: payload.id }),
        {
          loading: 'Archiving event...',
          success: 'Event archived',
          setErrors: options?.setErrors,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
    },
  });
};

export const useRestoreIncidentMutation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await withErrorHandling(
        api.v1.restoreIncidentResultV1IncidentResultsRestorePost({ id }),
        {
          loading: 'Restoring event...',
          success: 'Event restored',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_TYPES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INCIDENTS_LIST_QUERY_KEY]);
    },
  });
};
